import type { ReactNode } from 'react';
import { SiteLink as Link } from '../SiteLink';
import { ArrowRight } from '@phosphor-icons/react';
import { Painting } from '../Painting';
import type { PaintingName } from '../Painting';
import { AgentsWindow, ConfigWindow, FleetWindow, ManageAccessWindow, MANAGE_ACCESS } from '../product';
import { LiveAccessWindow, LiveDashboardWindow } from '../product/live';
import { Film } from './Video';
import { SIGNUP_URL } from '../../lib/appUrls';
import { linkProps } from '../../lib/links';

const CLIENTS = [
  'Claude Code',
  'Claude Desktop',
  'Cursor',
  'Windsurf',
  'VS Code',
  'Cline',
  'Continue',
  'Zed',
  'Amazon Q',
];

export function Hero() {
  return (
    <>
      <section className="l-hero" aria-labelledby="hero-title">
        <h1 id="hero-title" className="l-rise">
          Grant the <u>tool</u>, not the whole server.
        </h1>
        <div className="l-rise" style={{ animationDelay: '.08s' }}>
          <p className="l-lede">
            Statio is an MCP gateway that controls access one tool at a time and gives every AI agent its own identity.
            The desktop app sets up Claude Code, Cursor and seven more clients, Stripe and GitHub keys stay encrypted in
            Statio instead of on laptops, and every call is logged by agent and tool, allowed or refused.
          </p>
          <div className="l-ctas">
            <a className="s-btn s-btn-pri" href={SIGNUP_URL} target="_blank" rel="noopener noreferrer">
              Start free
            </a>
            <a className="s-btn s-btn-sec" href="#pricing">
              See pricing
            </a>
          </div>
          <p className="l-fine">
            Free plan, no card · New to MCP? <Link to="/tutorials/what-is-mcp">What is an MCP server?</Link> · Uses the
            Statio desktop app for <Link to="/download">macOS, Windows and Linux</Link> · Names are illustrative
          </p>
        </div>
      </section>
      <div className="l-film">
        <Film
          name="rome"
          label="Statio film: from the gates of ancient Rome to the AI agents calling your APIs today"
          transcript="Every road into Rome ended at a gate. At every gate stood a statio: a guard post where nobody passed without a name. Every traveller named, every cargo checked. Today the travellers are AI agents, making thousands of calls, most on a borrowed key. So we built the post again: Statio names every agent and checks every call. In the app, an admin denies db.deleteRows for ci-agent, and the agent's next call fails with a 403."
        />
      </div>
      <Painting name="aqueduct" className="l-cine">
        <div className="cap">
          <h2>Your agent can read your data. It can also delete it.</h2>
          <p>
            One MCP server holds selectRows and deleteRows side by side, and per-server access gives an agent both or
            neither. In Statio you grant the server, then deny the tool that deletes your data.
          </p>
        </div>
        <LiveAccessWindow />
      </Painting>
      <div className="l-clients">
        <p>Configures the AI clients your team already uses</p>
        <ul>
          {CLIENTS.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      </div>
    </>
  );
}

export function PerToolExplainer() {
  const annotated = MANAGE_ACCESS.tools.map((t) => ({
    ...t,
    note: t.name === 'db.selectRows' ? 2 : t.name === 'db.deleteRows' ? 3 : undefined,
  }));
  return (
    <section className="l-explain" id="per-tool" aria-labelledby="per-tool-title">
      <h2 id="per-tool-title">Say yes to a server. Say no to one of its tools.</h2>
      <p className="sub">
        Every MCP server bundles harmless tools with dangerous ones. Here is the difference per-tool access makes, using
        a Postgres server.
      </p>

      <div className="l-cmp">
        <div className="l-col old">
          <h3 className="hd">Most gateways: one switch per server</h3>
          <div className="l-srv">
            <b>postgres</b>
            <span className="l-sw" aria-hidden="true" />
            <span className="lab">Access granted</span>
          </div>
          <ul>
            <li>
              <span>db.selectRows</span>
              <span className="pw-tag pw-ok">allowed</span>
            </li>
            <li>
              <span>db.listTables</span>
              <span className="pw-tag pw-ok">allowed</span>
            </li>
            <li className="warn">
              <span>db.deleteRows</span>
              <span className="pw-tag pw-ok">allowed</span>
            </li>
            <li className="warn">
              <span>db.insertRows</span>
              <span className="pw-tag pw-ok">allowed</span>
            </li>
          </ul>
          <p className="res">
            One switch for the whole server. If the agent can read rows, it can delete and insert them too.
          </p>
        </div>
        <div className="l-col new">
          <h3 className="hd">Statio: a decision per tool</h3>
          <div className="l-srv">
            <b>postgres</b>
            <span className="l-sw" aria-hidden="true" />
            <span className="lab">Server access granted</span>
          </div>
          <ul>
            <li>
              <span>db.selectRows</span>
              <span className="pw-tag pw-ok">allowed</span>
            </li>
            <li>
              <span>db.listTables</span>
              <span className="pw-tag pw-ok">allowed</span>
            </li>
            <li>
              <span>db.deleteRows</span>
              <span className="pw-tag pw-no">denied</span>
            </li>
            <li>
              <span>db.insertRows</span>
              <span className="pw-tag pw-no">denied</span>
            </li>
          </ul>
          <p className="res">Grant the server, then deny the tools that change your data. The deny always wins.</p>
        </div>
      </div>

      <div className="l-ann">
        <Painting name="campagna">
          <ManageAccessWindow tools={annotated} annotate />
        </Painting>
        <ol className="l-steps">
          <li>
            <span className="pw-n">1</span>
            <div>
              <b>Pick the agent and the server</b>
              <p>ci-agent has been granted the postgres server. That one grant covers every tool on it.</p>
            </div>
          </li>
          <li>
            <span className="pw-n">2</span>
            <div>
              <b>Read tools ride on that grant</b>
              <p>selectRows and listTables show “via server access”. Nothing else to set up.</p>
            </div>
          </li>
          <li>
            <span className="pw-n">3</span>
            <div>
              <b>Deny the dangerous tool</b>
              <p>One click on Deny records a deny for deleteRows. It overrides the server grant for that tool only.</p>
            </div>
          </li>
          <li>
            <span className="pw-n">4</span>
            <div>
              <b>What the agent sees</b>
              <p>
                If ci-agent calls deleteRows, the gateway refuses it before any credential is used, and Activity records
                it:
              </p>
              <div className="act">
                <span className="pw-tag pw-no">failed</span> db.deleteRows · 403 ·{' '}
                <span className="err">{"You don't have permission to call tool 'db.deleteRows'"}</span>
              </div>
            </div>
          </li>
        </ol>
      </div>
    </section>
  );
}

interface Pain {
  id: string;
  pain: string;
  fix: string;
  art: PaintingName;
  screen: ReactNode;
  rows: [string, string][];
  more: { label: string; href: string };
}

const PAINS: Pain[] = [
  {
    id: 'agents',
    pain: 'Every agent is running on somebody’s personal key.',
    fix: 'Agents borrow a human’s token, so you can’t tell them apart and you can’t cut one off without breaking the person. In Statio every agent gets its own identity.',
    art: 'cascade',
    screen: <AgentsWindow />,
    rows: [
      ['A token per agent', 'Each agent authenticates as itself, never as the engineer who set it up.'],
      ['Suspend or revoke one', 'Stop nightly-sync without touching anyone else.'],
      ['An end date per agent', 'Set an expiry date and the agent’s token stops working on that date.'],
    ],
    more: { label: 'How agents work', href: 'https://docs.statio.dev/docs/' },
  },
  {
    id: 'activity',
    pain: 'When something goes wrong, nobody can say which agent did it.',
    fix: 'Statio records every tool call through the gateway: who called what, on which server, how long it took, and whether it was allowed.',
    art: 'sunrise',
    screen: <LiveDashboardWindow />,
    rows: [
      [
        'The agent’s name on every line',
        'Each call names the agent or the person behind it, the tool, the server and how long it took.',
      ],
      [
        'A 403 with the reason',
        'A blocked call shows the 403 and the message the agent got back, so a refusal is never a mystery.',
      ],
      ['Stream it to your SIEM', 'On Business, tool calls and audit events go out by signed webhook.'],
    ],
    more: { label: 'Audit and activity', href: '/security#records' },
  },
  {
    id: 'keys',
    pain: 'Live API keys are sitting in config files on laptops.',
    fix: 'Every developer’s AI client holds its own copy of your Stripe, GitHub and Slack keys. With Statio the laptop holds one Statio key, and the real credentials stay encrypted in Statio.',
    art: 'crescenza',
    screen: <ConfigWindow />,
    rows: [
      [
        'AES-256-GCM, added at call time',
        'Credentials are stored encrypted and attached inside the gateway, after the access check.',
      ],
      [
        'A lost laptop leaks a Statio key',
        'Revoke that one key in Statio. Your Stripe secret was never on the machine.',
      ],
      [
        'Claude Code, Cursor and seven more',
        'The desktop app writes each client’s MCP config, so nobody pastes a key into a JSON file.',
      ],
    ],
    more: { label: 'How Statio handles credentials', href: '/security' },
  },
  {
    id: 'fleet',
    pain: 'You don’t know what your team has already installed.',
    fix: 'Developers add MCP servers, plugins and skills on their own. Fleet, on the Team plan and up, finds them on every machine running the desktop app, without CrowdStrike, Intune or Jamf.',
    art: 'portico',
    screen: <FleetWindow />,
    rows: [
      ['Managed or External', 'See which servers route through Statio and which don’t.'],
      ['Plugins and skills too', 'MCP servers in all nine clients, plus Claude Code plugins and skills.'],
      ['Seen when it changes', 'A newly added server shows up within minutes while the desktop app is running.'],
    ],
    more: { label: 'How shadow discovery works', href: 'https://docs.statio.dev/docs/guides/shadow-ai/' },
  },
];

function More({ label, href }: { label: string; href: string }) {
  const p = linkProps(href);
  const inner = (
    <>
      {label} <ArrowRight size={16} weight="bold" />
    </>
  );
  return p.to ? (
    <Link className="s-link" to={p.to}>
      {inner}
    </Link>
  ) : (
    <a className="s-link" href={p.href} target="_blank" rel="noopener noreferrer">
      {inner}
    </a>
  );
}

export function PainSections() {
  return (
    <>
      {PAINS.map((s, i) => (
        <section key={s.id} id={s.id} className={i % 2 ? 'l-pain' : 'l-pain flip'} aria-labelledby={`${s.id}-title`}>
          <div className="txt">
            <h2 id={`${s.id}-title`}>{s.pain}</h2>
            <p className="fix">{s.fix}</p>
            <ul>
              {s.rows.map(([t, d]) => (
                <li key={t}>
                  <b>{t}</b>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
            <More {...s.more} />
          </div>
          <Painting name={s.art}>{s.screen}</Painting>
        </section>
      ))}
    </>
  );
}

export function CloseCTA() {
  return (
    <section className="l-end" aria-labelledby="close-title">
      <Painting name="ancient-rome">
        <h2 id="close-title">Deny deleteRows for your first agent in a few minutes.</h2>
        <p>Free plan, three servers, no card. If you outgrow it the next plan is $19.</p>
        <a className="s-btn" href={SIGNUP_URL} target="_blank" rel="noopener noreferrer">
          Start free
        </a>
      </Painting>
    </section>
  );
}
